// Safety nets and monitoring: runway conflicts, ground collisions, radar separation (3 NM in the TMA, 5 NM
// outside, 1000 ft), wake turbulence on final and for departures, minimum vectoring altitudes, missing
// hand-offs. The same logic drives the short-term conflict alert (STCA) on the radar display.
import type { World } from './world';
import type { Aircraft } from './aircraft';
import type { Pilot } from './pilot';
import { distOnFinal } from './aircraft';
import { NM, dist, DEG } from './geo';
import { wakeDepSec, wakeSepNm } from './perf';

export interface Conflict { a: string; b: string; dNm: number; dvFt: number; predicted: boolean }

export class SafetyMonitor {
  /** Current alerts for the display (cs pairs). */
  stca: Conflict[] = [];
  /** Aircraft below the minimum vectoring altitude. */
  msaw = new Set<string>();
  private active = new Set<string>();
  private acc = 0;
  private handoffWarned = new Set<string>();

  constructor(private readonly w: World) {}

  /** Incidents caused while only AI controllers were responsible (used by the bot tests). */
  aiIncidents: string[] = [];
  private lastAcs: Aircraft[] = [];
  private responsible(...acs: Aircraft[]): boolean {
    this.lastAcs = acs;
    const units = this.w.playerUnits();
    return acs.some(a => {
      const p = this.w.pilot(a.cs);
      return !!p?.freq && units.includes(p.freq);
    });
  }

  update(dt: number): void {
    this.acc += dt;
    if (this.acc < 1) return;
    this.acc = 0;
    this.runways();
    this.ground();
    this.airborne();
    this.handoffs();
  }

  // ------------------------------------------------------------------ runway
  private runways(): void {
    const w = this.w;
    for (const rw of w.airport.runways) {
      const occ = w.runwayOccupants(rw.id);
      if (occ.length < 2) continue;
      const fast = occ.filter(a => (a.onGround && (a.gmode === 'TAKEOFF' || a.gmode === 'ROLLOUT') && a.ias > 40) || (!a.onGround && a.altFt - w.meta.elevFt < 150));
      for (const f of fast) {
        for (const o of occ) {
          if (o === f) continue;
          // traffic behind a departing aircraft is not in its way
          const endId = f.rwy?.id ?? w.pilot(f.cs)?.arrRwy ?? w.pilot(f.cs)?.depRwy ?? null;
          const end = endId ? w.airport.runwayEnds[endId] : undefined;
          if (end) {
            const af = w.airport.along(end, f), ao = w.airport.along(end, o);
            if (ao < af - 30) continue;
          }
          const key = `RWY|${[f.cs, o.cs].sort().join('|')}`;
          if (this.active.has(key)) continue;
          this.active.add(key);
          const landing = !f.onGround || f.gmode === 'ROLLOUT';
          if (!this.responsible(f, o)) this.aiIncidents.push(`${this.w.t.toFixed(0)}s ${[f, o].map(x => x.cs).join("/")}`);
          else {
            w.event('safety', 'critical', landing ? 'LAND_OCCUPIED' : 'TAKEOFF_OCCUPIED',
              landing ? `${f.cs} landet auf der Piste ${rw.id}, obwohl ${o.cs} sich noch darauf befindet.`
                : `${f.cs} startet auf der Piste ${rw.id}, obwohl ${o.cs} sich noch darauf befindet.`, f.cs);
          }
        }
      }
    }
  }

  /** Called when a take-off roll starts: departure interval and wake turbulence. */
  onTakeoff(p: Pilot): void {
    const w = this.w, last = w.lastDeparture;
    if (!last || last.cs === p.cs) return;
    const elapsed = w.t - last.t;
    const need = wakeDepSec(last.wake, p.ac.type.wake, !!p.depHold && p.depHold !== w.airport.runwayEnds[p.depRwy ?? '']?.depHolds[0]);
    if (need && elapsed < need - 5 && !this.responsible(p.ac)) this.aiIncidents.push(`${w.t.toFixed(0)}s WAKE_DEP ${p.cs}`);
    else if (need && elapsed < need - 5) {
      w.event('safety', 'major', 'WAKE_DEP', `Wirbelschleppen: ${p.cs} (${p.ac.type.wake}) startet nur ${Math.round(elapsed)} s nach ${last.cs} (${last.wake}), nötig sind ${need} s.`, p.cs);
    }
  }

  // ------------------------------------------------------------------ ground
  private ground(): void {
    const w = this.w;
    const g = w.aircraft.filter(a => a.onGround);
    for (let i = 0; i < g.length; i++) {
      for (let j = i + 1; j < g.length; j++) {
        const a = g[i]!, b = g[j]!;
        if (a.gmode === 'PARKED' && b.gmode === 'PARKED') continue;
        const ca = { x: a.x - Math.sin(a.hdg * DEG) * a.type.lengthM * 0.45, y: a.y - Math.cos(a.hdg * DEG) * a.type.lengthM * 0.45 };
        const cb = { x: b.x - Math.sin(b.hdg * DEG) * b.type.lengthM * 0.45, y: b.y - Math.cos(b.hdg * DEG) * b.type.lengthM * 0.45 };
        const d = dist(ca.x, ca.y, cb.x, cb.y);
        const lim = (a.type.spanM + b.type.spanM) * 0.3 + Math.min(a.type.lengthM, b.type.lengthM) * 0.15;
        const key = `GND|${[a.cs, b.cs].sort().join('|')}`;
        if (d < lim) {
          if (!this.active.has(key) && (a.ias > 1 || b.ias > 1)) {
            this.active.add(key);
            if (!this.responsible(a, b)) { this.aiIncidents.push(`${w.t.toFixed(0)}s GROUND ${a.cs}/${b.cs}`); continue; }
            w.event('safety', 'critical', 'GROUND_COLLISION', `Zusammenstoß am Boden zwischen ${a.cs} und ${b.cs}.`, a.cs);
          }
        } else if (d > lim * 2) this.active.delete(key);
      }
    }
  }

  // ------------------------------------------------------------------ airborne
  private airborne(): void {
    const w = this.w;
    const air = w.aircraft.filter(a => !a.onGround && a.altFt - w.meta.elevFt > 300);
    const stca: Conflict[] = [];
    for (let i = 0; i < air.length; i++) {
      for (let j = i + 1; j < air.length; j++) {
        const a = air[i]!, b = air[j]!;
        const dNm = dist(a.x, a.y, b.x, b.y) / NM;
        const dv = Math.abs(a.altFt - b.altFt);
        const inTma = dist(a.x, a.y, 0, 0) < 30 * NM && dist(b.x, b.y, 0, 0) < 30 * NM;
        const minH = inTma ? 3 : 5;
        // both established on the same localizer: in-trail separation incl. wake turbulence
        const pa = w.pilot(a.cs), pb = w.pilot(b.cs);
        const sameFinal = a.locCap && b.locCap && pa?.arrRwy && pa.arrRwy === pb?.arrRwy;
        let need = minH;
        let wake = false;
        if (sameFinal) {
          const end = w.airport.end(pa.arrRwy!);
          const da = distOnFinal(a, end), db = distOnFinal(b, end);
          const [lead, foll] = da < db ? [a, b] : [b, a];
          const ws = wakeSepNm(lead.type.wake, foll.type.wake);
          if (ws > need) { need = ws; wake = true; }
          // reduced separation on the final 10 NM is not modelled; 3 NM applies
        }
        const key = `SEP|${[a.cs, b.cs].sort().join('|')}`;
        if (dNm < need - 0.05 && dv < 800) {
          stca.push({ a: a.cs, b: b.cs, dNm, dvFt: dv, predicted: false });
          if (!this.active.has(key)) {
            this.active.add(key);
            if (!this.responsible(a, b)) this.aiIncidents.push(`${w.t.toFixed(0)}s SEP ${a.cs}/${b.cs} ${dNm.toFixed(1)}NM ${Math.round(dv)}ft${wake ? ' wake' : ''}`);
            else {
              const nearMiss = dNm < 1 && dv < 500;
              w.event('safety', nearMiss ? 'critical' : 'major', wake ? 'WAKE_FINAL' : nearMiss ? 'NEAR_MISS' : 'SEPARATION',
                wake ? `Wirbelschleppenstaffelung auf dem Endanflug unterschritten: ${a.cs} / ${b.cs} (${dNm.toFixed(1)} NM, nötig ${need} NM).`
                  : nearMiss ? `Gefährliche Annäherung (TCAS RA): ${a.cs} und ${b.cs}, ${dNm.toFixed(1)} NM / ${Math.round(dv)} ft.`
                    : `Staffelungsunterschreitung: ${a.cs} und ${b.cs}, ${dNm.toFixed(1)} NM / ${Math.round(dv)} ft (Minimum ${minH} NM oder 1000 ft).`, a.cs);
            }
          }
        } else {
          if (dNm > need + 0.5 || dv > 1100) this.active.delete(key);
          // prediction (2 minutes, linear)
          const t = 120;
          const ax = a.x + Math.sin(a.trk * DEG) * a.gs * 0.5144 * t, ay = a.y + Math.cos(a.trk * DEG) * a.gs * 0.5144 * t;
          const bx = b.x + Math.sin(b.trk * DEG) * b.gs * 0.5144 * t, by = b.y + Math.cos(b.trk * DEG) * b.gs * 0.5144 * t;
          const fa = a.altFt + a.vs * 2, fb = b.altFt + b.vs * 2;
          const minD = segMinDist(a.x, a.y, ax, ay, b.x, b.y, bx, by) / NM;
          if (minD < minH && Math.abs(fa - fb) < 950 && dNm < 15 && !sameFinal) stca.push({ a: a.cs, b: b.cs, dNm: minD, dvFt: Math.abs(fa - fb), predicted: true });
        }
      }
    }
    this.stca = stca;
    // minimum vectoring altitude for aircraft on radar headings
    this.msaw.clear();
    for (const a of air) {
      const p = w.pilot(a.cs);
      if (!p || a.amode !== 'HDG' || a.locCap || a.goAround) continue;
      if (p.isDeparture && dist(a.x, a.y, 0, 0) < 6 * NM) continue;
      const r = dist(a.x, a.y, 0, 0) / NM;
      const mva = w.meta.mva.find(m => r <= m.radiusNm)?.ft ?? 3000;
      if (a.altFt < mva - 100 && a.tgtAltFt < mva - 100) {
        this.msaw.add(a.cs);
        const key = `MVA|${a.cs}`;
        if (!this.active.has(key)) {
          this.active.add(key);
          if (!this.responsible(a)) { this.aiIncidents.push(`${w.t.toFixed(0)}s MVA ${a.cs}`); continue; }
          w.event('safety', 'major', 'MVA', `${a.cs} unter Radarführung unterhalb der Mindestradarhöhe (${Math.round(a.altFt)} ft, MVA ${mva} ft).`, a.cs);
        }
      }
    }
  }

  // ------------------------------------------------------------------ hand-offs
  private handoffs(): void {
    const w = this.w;
    for (const a of w.aircraft) {
      const p = w.pilot(a.cs);
      if (!p?.freq || this.handoffWarned.has(a.cs)) continue;
      const roles = w.unitRoles(p.freq);
      const mine = w.playerUnits().includes(p.freq);
      if (!mine) continue;
      // arrival on final still with approach
      if (!a.onGround && p.arrRwy && !roles.includes('TWR') && a.locCap && distOnFinal(a, w.airport.end(p.arrRwy)) < 4 && !p.isDeparture) {
        this.handoffWarned.add(a.cs);
        w.event('comms', 'major', 'NO_HANDOFF_TWR', `${a.cs} ist auf 4 NM Endanflug noch nicht an den Tower übergeben.`, a.cs);
      }
      // departure leaving the sector
      const unit = w.unit(p.freq);
      if (!a.onGround && p.isDeparture && unit.sector && dist(a.x, a.y, 0, 0) > unit.sector.radiusNm * NM) {
        this.handoffWarned.add(a.cs);
        w.event('comms', 'minor', 'NO_HANDOFF', `${a.cs} verlässt deinen Sektor ohne Übergabe.`, a.cs);
      }
      // departure airborne still on tower
      if (!a.onGround && p.isDeparture && roles.includes('TWR') && a.altFt - w.meta.elevFt > 3000) {
        this.handoffWarned.add(a.cs);
        w.event('comms', 'minor', 'NO_HANDOFF_DEP', `${a.cs} ist schon über 3000 ft und noch auf der Tower-Frequenz.`, a.cs);
      }
    }
  }
}

function segMinDist(ax: number, ay: number, bx: number, by: number, cx: number, cy: number, dx: number, dy: number): number {
  // both points move linearly from (a→b) and (c→d) over the same time: minimum distance of the relative motion
  const rx = cx - ax, ry = cy - ay;
  const vx = (dx - cx) - (bx - ax), vy = (dy - cy) - (by - ay);
  const vv = vx * vx + vy * vy;
  const t = vv > 0 ? Math.max(0, Math.min(1, -(rx * vx + ry * vy) / vv)) : 0;
  return Math.hypot(rx + vx * t, ry + vy * t);
}
